import { Link, useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { 
  Menu, 
  X, 
  Home, 
  Music, 
  ShoppingBag, 
  Calendar, 
  Bath,
  ShoppingCart,
  User,
  Database,
  Shield,
  ChevronDown,
  Settings,
  LogOut,
  Zap,
  Bell,
  Globe
} from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { UserMenu } from "@/components/auth/UserMenu";
import { LoginForm } from "@/components/auth/LoginForm";
import { LoginModal } from "@/components/auth/LoginModal";
import { NotificationBell } from "@/components/notification-panel";
import { NavigationMenuLink } from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavigationProps {
  onCartClick: () => void;
}

export function Navigation({ onCartClick }: NavigationProps) {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.getInfo().then(info => {
        setAppVersion(info.version);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsAccountMenuOpen(false);
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  // Initialize auth with safe defaults
  let isAuthenticated = false;
  let logout: any = null;
  let isLoading = true;
  let user: any = null;

  try {
    const authContext = useAuth();
    if (authContext) {
      ({ isAuthenticated = false, logout, isLoading = true, user = null } = authContext);
    }
  } catch (error) {
    console.error('Auth context error:', error);
  }

  const handleLogout = async () => {
    if (logout) {
      await logout();
    }
  };

  const isActive = (path: string) => {
    return location === path;
  };

  // Show admin menu only for admin users
  const showAdminMenu = isAuthenticated && user?.role === 'admin';

  const { data: cartItems = [] } = useQuery({
    queryKey: ["/api/cart"],
  });

  const publicNavigationItems = [
    { href: "/", label: "Home", icon: Home },
    { href: "/sound-therapy", label: "Calm Sounds", icon: Music },
    { href: "/mindfulness", label: "Mindfulness", icon: Bath },
    { href: "/therapists", label: "Mitr", icon: User },
    { href: "/booking", label: "Book Session", icon: Calendar },
    // { href: "/shop", label: "Shop", icon: ShoppingBag }, // Temporarily hidden
  ];

  const navigationItems = publicNavigationItems;

  const publicMobileNavItems = [
    { href: "/", label: "Home", icon: Home },
    { href: "/sound-therapy", label: "Calm", icon: Music },
    { href: "/mindfulness", label: "Mindfulness", icon: Bath },
    { href: "/booking", label: "Consult", icon: User },
  ];

  const authenticatedMobileNavItems = [
    { href: "/my-account/sessions", label: "Sessions", icon: Calendar },
  ];

  const mobileNavItems = [
    ...publicMobileNavItems,
    ...(isAuthenticated ? authenticatedMobileNavItems : [])
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link href="/">
              <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
                  <img src="/owl-logo.png" alt="HealthyOwl Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                </div>
                <span className="text-lg sm:text-2xl font-bold text-primary font-heading">
                  Healthy Owl<sup className="text-xs ml-0.5">{appVersion ? `v${appVersion}` : 'Beta'}</sup>
                </span>
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navigationItems.map((item) => (
                <Link key={item.href} href={item.href}>
                  <span className={cn(
                    "font-medium transition-all cursor-pointer px-3 py-2 rounded-md text-sm xl:text-base",
                    location === item.href 
                      ? "text-primary bg-primary/10" 
                      : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  )}>
                    {item.label}
                  </span>
                </Link>
              ))}

              {/* My Account Link - Only shown when authenticated */}
              {isAuthenticated && (
                <Link href="/my-account/sessions">
                  <span className={cn(
                    "font-medium transition-all cursor-pointer px-3 py-2 rounded-md text-sm xl:text-base",
                    (location.startsWith("/my-account") || location === "/my-bookings")
                      ? "text-primary bg-primary/10" 
                      : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  )}>
                    My Account
                  </span>
                </Link>
              )}

              {/* Cart button temporarily hidden
              <Button
                variant="outline"
                size="sm"
                onClick={onCartClick}
                className="relative mr-2"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                <span className="hidden xl:inline">Cart</span>
                {Array.isArray(cartItems) && cartItems.length > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {cartItems.length}
                  </Badge>
                )}
              </Button>
              */}

              {!isLoading && (
                isAuthenticated ? (
                  <>
                    <NotificationBell />
                    <UserMenu />
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsLoginOpen(true)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      <span className="hidden xl:inline">Login/Signup</span>
                    </Button>
                    <LoginModal 
                      open={isLoginOpen} 
                      onOpenChange={setIsLoginOpen}
                    />
                  </>
                )
              )}
            </nav>

            {/* Tablet Navigation */}
            <nav className="hidden md:flex lg:hidden items-center space-x-4">
              {navigationItems.slice(0, 4).map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.href} href={item.href}>
                    <div className={cn(
                      "flex flex-col items-center p-2 rounded-md transition-all cursor-pointer",
                      location === item.href 
                        ? "text-primary bg-primary/10" 
                        : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                    )}>
                      <Icon className="h-5 w-5" />
                      <span className="text-xs mt-1">{item.label.split(' ')[0]}</span>
                    </div>
                  </Link>
                );
              })}

              {/* Cart button temporarily hidden
              <Button
                variant="outline"
                size="sm"
                onClick={onCartClick}
                className="relative flex flex-col items-center p-2 mr-2"
              >
                <ShoppingCart className="h-5 w-5" />
                <span className="text-xs mt-1">Cart</span>
                {Array.isArray(cartItems) && cartItems.length > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-xs"
                  >
                    {cartItems.length}
                  </Badge>
                )}
              </Button>
              */}

              {!isLoading && (
                isAuthenticated ? (
                  <>
                    <NotificationBell />
                    <UserMenu />
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex flex-col items-center p-2"
                      onClick={() => setIsLoginOpen(true)}
                    >
                      <User className="h-5 w-5" />
                      <span className="text-xs mt-1">Login</span>
                    </Button>
                    <LoginModal 
                      open={isLoginOpen} 
                      onOpenChange={setIsLoginOpen}
                    />
                  </>
                )
              )}
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-1 md:hidden">
              {isAuthenticated && <NotificationBell />}

              <Button
                ref={menuButtonRef}
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 bg-primary/10 hover:bg-primary/20 rounded-md"
              >
                {isMobileMenuOpen ? (
                  <X className="h-[56px] w-[56px] text-primary" />
                ) : (
                  <Menu className="h-[56px] w-[56px] text-primary" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden bg-background border-t border-border">
            <nav className="px-4 py-4 space-y-2">
              {!isLoading && isAuthenticated && user ? (
                <div>
                  <div 
                    className="flex items-center justify-between px-3 py-4 cursor-pointer"
                    onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
                        {user.firstName && user.lastName
                          ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                          : user.firstName
                          ? user.firstName.substring(0, 2).toUpperCase()
                          : user.email
                          ? user.email.split('@')[0].substring(0, 2).toUpperCase()
                          : 'U'}
                      </div>
                      <div className="truncate">
                        <p className="text-base font-semibold truncate">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.firstName
                            ? user.firstName
                            : user.email
                            ? user.email.split('@')[0]
                            : 'User'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{user.email || ''}</p>
                      </div>
                    </div>
                    <Settings className={cn("h-5 w-5 text-muted-foreground transition-transform", isAccountMenuOpen && "rotate-90")} />
                  </div>
                  {isAccountMenuOpen && (
                    <div className="pl-6 space-y-1 pb-2 bg-muted/50 rounded-lg mx-2 py-2">
                      <Link href="/my-account/sessions">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <Calendar className="h-4 w-4" />
                          <span className="font-medium text-sm">My Sessions</span>
                        </div>
                      </Link>
                      <Link href="/my-account/subscription">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <Zap className="h-4 w-4" />
                          <span className="font-medium text-sm">My Subscription</span>
                        </div>
                      </Link>
                      <Link href="/my-account/profile">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <Settings className="h-4 w-4" />
                          <span className="font-medium text-sm">My Profile</span>
                        </div>
                      </Link>
                      <Link href="/my-account/notifications">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <Bell className="h-4 w-4" />
                          <span className="font-medium text-sm">Notification Settings</span>
                        </div>
                      </Link>
                      <Link href="/my-account/language">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <Globe className="h-4 w-4" />
                          <span className="font-medium text-sm">Language</span>
                        </div>
                      </Link>
                      <Link href="/my-account/security">
                        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(false)}>
                          <User className="h-4 w-4" />
                          <span className="font-medium text-sm">Security</span>
                        </div>
                      </Link>
                      <div 
                        className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                        onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="font-medium text-sm">Log out</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : !isLoading ? (
                <div className="flex items-center justify-center py-4 px-3">
                  <Button 
                    className="bg-primary hover:bg-primary/90 text-white font-medium px-6"
                    onClick={() => setIsLoginOpen(true)}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Sign In
                  </Button>
                  <LoginModal 
                    open={isLoginOpen} 
                    onOpenChange={setIsLoginOpen}
                  />
                </div>
              ) : null}
              {[
                { href: "/", label: "Home", icon: Home },
                { href: "/therapists", label: "Book Human Mitr Session", icon: User },
                { href: "/booking", label: "Book AI Mitr Session", icon: Calendar },
              ].map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link key={item.href} href={item.href}>
                    <div 
                      className={cn(
                        "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer",
                        location === item.href 
                          ? "text-primary bg-primary/10" 
                          : "text-muted-foreground hover:bg-accent"
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="border-t border-border my-2"></div>

              {/* Support & Info Section */}
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Support & Info</h3>
              </div>

              <Link href="/about">
                <div 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-medium">About Us</span>
                </div>
              </Link>

              <Link href="/about-health-ai">
                <div 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-medium">HealthyOwl AI</span>
                </div>
              </Link>

              <Link href="/contact">
                <div 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-medium">Contact Us</span>
                </div>
              </Link>

              <Link href="/terms-privacy-policy">
                <div 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="font-medium">Privacy Policy & Terms</span>
                </div>
              </Link>

              {/* WhatsApp Contact */}
              <a href="https://wa.me/919911090091?text=Hi%20Team%20Healthy%20Owl" target="_blank" rel="noopener noreferrer">
                <div 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <SiWhatsapp className="h-5 w-5 text-green-500" />
                  <span className="font-medium">WhatsApp Us</span>
                </div>
              </a>

              {/* Divider */}
              <div className="border-t border-border my-2"></div>

              {/* App Version */}
              <div className="px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">App Version</span>
                  <span className="text-sm font-medium text-primary">{appVersion ? `v${appVersion}` : 'Responsive Beta'}</span>
                </div>
              </div>

              {/* Cart temporarily hidden
              <div 
                className="flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer text-muted-foreground hover:bg-accent"
                onClick={() => {
                  onCartClick();
                  setIsMobileMenuOpen(false);
                }}
              >
                <ShoppingCart className="h-5 w-5" />
                <span className="font-medium">
                  Cart {cartItems.length > 0 && `(${cartItems.length})`}
                </span>
              </div>
              */}
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-40">
        <div className="flex items-center justify-around py-2">
          {mobileNavItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location === item.href;

            return (
              <Link key={item.href} href={item.href}>
                <div className={cn(
                  "flex flex-col items-center py-2 px-3 transition-all cursor-pointer rounded-md",
                  isActive 
                    ? "text-primary bg-primary/10" 
                    : "text-muted-foreground hover:text-primary"
                )}>
                  <IconComponent className="h-6 w-6 mb-1" />
                  <span className="text-xs font-medium">{item.label}</span>
                </div>
              </Link>
            );
          })}

          {!isLoading && !isAuthenticated && (
            <>
              <div 
                className={cn(
                  "flex flex-col items-center py-2 px-3 transition-colors cursor-pointer",
                  "text-muted-foreground"
                )}
                onClick={() => setIsLoginOpen(true)}
              >
                <User className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium">Login</span>
              </div>
              <LoginModal 
                open={isLoginOpen} 
                onOpenChange={setIsLoginOpen}
              />
            </>
          )}
        </div>
      </nav>
    </>
  );
}